/**
 * What a card's two buttons do (twin slice 1, plan W5 step 14).
 *
 *   "Show where"         a LINK: the Overview's own locate push
 *                        (`showWhereHref` with a fresh landing id, as the
 *                        toasts and drawer headers push it), from the
 *                        attention list. It lands -- the floor, the ring,
 *                        the callout -- and never opens a drawer: a landing
 *                        for another subject closes the open one, if
 *                        anything (`landingClosesDrawer`).
 *   "Open <name> panel"  a tap the person makes, through the host's own
 *                        handler, as the callout's button and a tap on the
 *                        canvas are.
 *
 * Only a card about ONE robot, ONE car or ONE door has either
 * (`AttentionCard`): a site row is about no one place, and a card about two
 * robots names no single panel. Both answer null for those, so nothing is
 * pushed or opened.
 */
import type { LocateFocus } from '../../../state/locate';
import type { AttentionSubject } from '../../../state/attention/select';
import type { CalloutOpen } from '../overview-landing';

/** The one robot a subject is about, or null. */
function oneRobot(subject: AttentionSubject) {
  return subject.kind === 'robot' && subject.robots.length === 1 ? subject.robots[0]! : null;
}

/**
 * Where a card's "Show where" points: the car, the door, or the robot by
 * its fleet-qualified name (a link never carries a bare robot name: two
 * fleets may hold the same one). Null for a site row or several robots.
 */
export function attentionFocus(subject: AttentionSubject): LocateFocus | null {
  if (subject.kind === 'lift') return { kind: 'lift', liftName: subject.lift };
  if (subject.kind === 'door') return { kind: 'door', doorName: subject.door };
  const robot = oneRobot(subject);
  return robot ? { kind: 'robot', fleetName: robot.fleet, robotName: robot.name } : null;
}

/**
 * The panel a card's "Open <name> panel" opens, in the callout's terms:
 * the host keys a robot by its BARE name. Null for a site row or several
 * robots.
 */
export function attentionPanel(subject: AttentionSubject): CalloutOpen | null {
  if (subject.kind === 'lift') return { kind: 'lift', id: subject.lift, label: subject.lift };
  if (subject.kind === 'door') return { kind: 'door', id: subject.door, label: subject.door };
  const robot = oneRobot(subject);
  return robot ? { kind: 'robot', id: robot.name, label: robot.name } : null;
}
